"use client";

import type { Variants } from "motion/react";
import { motion, useAnimation } from "motion/react";
import type { HTMLAttributes } from "react";
import React, { forwardRef, useCallback, useImperativeHandle } from "react";

import { cn } from "@/lib/utils";
import { useHoverResetGuard } from "./useHoverResetGuard";

export interface GalleryVerticalEndIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface GalleryVerticalEndIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  /** e.g. `inherit` from sidebar route row so `currentColor` follows the nav icon color */
  color?: string;
}

const PATH_VARIANTS: Variants = {
  normal: {
    translateY: 0,
    opacity: 1,
    transition: {
      type: "tween",
      stiffness: 200,
      damping: 13,
    },
  },
  animate: (custom: number) => ({
    translateY: [2 * custom, 0],
    opacity: [0, 1],
    transition: {
      delay: 0.25 * (2 - custom),
      type: "tween",
      stiffness: 200,
      damping: 13,
    },
  }),
};

const GalleryVerticalEndIcon = forwardRef<
  GalleryVerticalEndIconHandle,
  GalleryVerticalEndIconProps
>(
  (
    {
      onMouseEnter,
      onMouseLeave,
      className,
      size = 20,
      color,
      style,
      ...props
    },
    ref
  ) => {
    const controls = useAnimation();
    const { withLockedHoverReset, releaseHoverResetLock, shouldApplyHoverReset } = useHoverResetGuard();

    useImperativeHandle(
      ref,
      () => ({
        startAnimation: () =>
          void withLockedHoverReset(async () => {
            await controls.start("animate");
          }),
        stopAnimation: () => {
          releaseHoverResetLock();
          void controls.start("normal");
        },
      }),
      [controls, releaseHoverResetLock, withLockedHoverReset]
    );

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        onMouseEnter?.(e);
        void controls.start("animate");
      },
      [controls, onMouseEnter]
    );

    const handleMouseLeave = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        onMouseLeave?.(e);
        if (!shouldApplyHoverReset()) return;
        void controls.start("normal");
      },
      [controls, onMouseLeave, shouldApplyHoverReset]
    );

    return (
      <div
        className={cn(className)}
        style={{ color: color ?? undefined, ...style }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        <svg
          fill="none"
          height={size}
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width={size}
          xmlns="http://www.w3.org/2000/svg"
        >
          <motion.path
            animate={controls}
            custom={1}
            d="M7 2h10"
            initial={{ translateY: 0, opacity: 1 }}
            variants={PATH_VARIANTS}
          />
          <motion.path
            animate={controls}
            custom={2}
            d="M5 6h14"
            initial={{ translateY: 0, opacity: 1 }}
            variants={PATH_VARIANTS}
          />
          <rect fill="none" height="12" rx="2" stroke="currentColor" width="18" x="3" y="10" />
        </svg>
      </div>
    );
  }
);

GalleryVerticalEndIcon.displayName = "GalleryVerticalEndIcon";

export { GalleryVerticalEndIcon };
